import { Alert, Badge, Button, Col, Modal, Row } from "antd";
import FeatureComingSoon from "../../common/FeatureComingSoon";
import gmail from "../../assets/images/auth/gmail.svg";
import microsoft from "../../assets/images/auth/microsoft.png";
import { useCalendarIntegration } from "./hooks/useCalendarIntegration";
import { useState } from "react";
import paperShredder from "../../assets/images/webToLead/paper-shredder.svg";
import { useSelector } from "react-redux";

const Integrations = () => {
  const { handleConnect, handleDisconnect, handleLatestSync } =
    useCalendarIntegration();
  const [isDisconnect, setDisconnect] = useState(false);
  const [type, setType] = useState();
  const summary = useSelector((state) => state.calendarReducer.integrationSummary);

  const isGoogleConnected =
    summary?.sources?.find((d) => d?.name === "Google Calendar")?.status ===
    "Connected";

  const isOutlookConnected =
    summary?.sources?.find((d) => d?.name === "Microsoft Calendar")?.status ===
    "Connected";

  const isGoogleMandatoryPending = !isGoogleConnected;

  const importMethods = [
    {
      title: "Google Calendar",
      description: "Sync with Google Calendar",
      icon: gmail,
      iconBg: "fff",
      status: isGoogleConnected ? "Connected" : "Not Connected",
      action: isGoogleConnected ? "Disconnect" : "Connect",
      isSyncLatest: !!isGoogleConnected,
      onClick: () => {
        if (isGoogleConnected) {
          setDisconnect(true);
          setType("Google");
        } else {
          handleConnect("Google");
        }
      },
      onSync: () => {
        handleLatestSync("Google");
      },
    },
    {
      title: "Outlook",
      description: "Sync with Microsoft Outlook",
      icon: microsoft,
      iconBg: "fff",
      status: isOutlookConnected ? "Connected" : "Not Connected",
      action: isOutlookConnected ? "Disconnect" : "Connect",
      isSyncLatest: !!isOutlookConnected,
      isDisabled: !isGoogleConnected && !isOutlookConnected,
      helperText:
        !isGoogleConnected && !isOutlookConnected
          ? "Connect Google Calendar first."
          : "",
      onClick: () => {
        if (isOutlookConnected) {
          setDisconnect(true);
          setType("Outlook");
        } else {
          handleConnect("Outlook");
        }
      },
      onSync: () => {
        handleLatestSync("Outlook");
      },
    },
  ];

  return (
    <>
      <div className="tw-mt-6 tw-mb-6">
        <h2 className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-1">
          Calendar Integrations
        </h2>
        <p className="tw-text-sm tw-text-slate-500">
          Connect your existing calendars to sync availability and bookings.
        </p>
      </div>

      <Alert
        className="tw-mb-8 tw-rounded-xl"
        type={isGoogleMandatoryPending ? "warning" : "info"}
        showIcon
        message={
          <span className="tw-font-bold tw-text-sm">
            Google Calendar integration is mandatory.
          </span>
        }
        description={
          <span className="tw-text-xs tw-block tw-mt-0.5">
            {isGoogleMandatoryPending
              ? "Please connect Google Calendar first to enable calendar management integrations."
              : "Google Calendar is connected and ready. You can manage additional calendar integrations below."}
          </span>
        }
      />

      <Row gutter={[20, 20]}>
        {importMethods.map((method, index) => {
          return (
            <Col xs={24} sm={12} lg={8} key={index}>
              <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-100 tw-rounded-2xl tw-shadow-[0_4px_20px_rgba(0,0,0,0.02)] hover:tw-shadow-[0_12px_30px_rgba(93,68,255,0.08)] hover:tw-scale-[1.01] tw-transition-all tw-duration-300 tw-p-6 tw-flex tw-flex-col tw-justify-between tw-h-full tw-gap-6">
                <div className="tw-flex tw-items-start tw-gap-4">
                  <Badge
                    dot
                    color={method.status === "Connected" ? "#10b981" : "#ef4444"}
                    offset={[-2, 2]}
                    className="big-dot"
                  >
                    <div className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-xl tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-100/60 tw-shadow-sm">
                      <img src={method.icon} alt={method.title} className="tw-h-6 tw-w-6 tw-object-contain" />
                    </div>
                  </Badge>

                  <div>
                    <h3 className="tw-text-sm tw-font-bold tw-text-slate-800 tw-mb-0.5">
                      {method.title}
                    </h3>
                    <p className="tw-text-xs tw-text-slate-400 tw-mb-0">
                      {method.description}
                    </p>
                    {method.helperText && (
                      <p className="tw-text-[11px] tw-font-semibold tw-text-amber-600 tw-mt-1.5 tw-mb-0">
                        {method.helperText}
                      </p>
                    )}
                  </div>
                </div>

                <div className="tw-flex tw-items-center tw-justify-between tw-mt-4 tw-gap-2">
                  {method.isSyncLatest ? (
                    <Button
                      className="!tw-h-[36px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-slate-50 !tw-px-4 !tw-text-xs !tw-font-bold !tw-text-slate-600 hover:!tw-border-slate-300 hover:!tw-bg-slate-100 tw-transition-all tw-duration-200"
                      onClick={method.onSync}
                    >
                      Sync Latest Contacts
                    </Button>
                  ) : (
                    <div />
                  )}

                  {method.onClick ? (
                    <Button
                      type="primary"
                      onClick={method.onClick}
                      disabled={method.isDisabled}
                      className={
                        method.action === "Disconnect"
                          ? "!tw-h-[36px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-4 !tw-text-xs !tw-font-bold !tw-text-red-500 hover:!tw-border-red-200 hover:!tw-bg-red-50 tw-transition-all tw-duration-200"
                          : "theme-btn !tw-h-[36px] !tw-rounded-lg !tw-px-4 !tw-text-xs !tw-font-bold tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] tw-transition-all tw-duration-200"
                      }
                    >
                      {method.action}
                    </Button>
                  ) : (
                    <FeatureComingSoon>
                      <Button
                        type="primary"
                        className="theme-btn !tw-h-[36px] !tw-rounded-lg !tw-px-4 !tw-text-xs !tw-font-bold"
                      >
                        {method.action}
                      </Button>
                    </FeatureComingSoon>
                  )}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>

      <Modal
        title={
          <div className="tw-text-lg tw-font-bold tw-text-slate-800 tw-px-6 tw-pt-6 tw-pb-2">
            Disconnect Calendar
          </div>
        }
        className="delete-popup"
        open={isDisconnect}
        centered
        footer={null}
        onCancel={() => {
          setDisconnect(false);
          setType();
        }}
      >
        <div className="tw-px-6 tw-pb-6">
          <div className="tw-flex tw-items-start tw-gap-6 tw-my-4">
            <div className="tw-flex tw-h-16 tw-w-16 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-red-50 tw-border tw-border-solid tw-border-red-100/50">
              <img src={paperShredder} alt="" className="tw-h-8 tw-w-8 tw-object-contain" />
            </div>
            <div className="tw-text-slate-600 tw-text-sm tw-leading-relaxed">
              <div className="tw-font-bold tw-text-slate-800 tw-mb-1.5">
                Are you sure you want to disconnect?
              </div>
              <div>
                Removing your account will halt all future event tracking and availability synchronization.
              </div>
            </div>
          </div>
          
          <div className="tw-flex tw-justify-end tw-align-items-center tw-gap-3 tw-mt-8">
            <Button
              className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
              onClick={() => {
                setDisconnect(false);
                setType();
              }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="!tw-h-[38px] !tw-rounded-lg !tw-border-0 !tw-bg-red-500 !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-white tw-shadow-[0_4px_12px_rgba(239,68,68,0.2)] hover:tw-shadow-[0_6px_16px_rgba(239,68,68,0.3)] hover:!tw-bg-red-600 tw-transition-all tw-duration-200"
              onClick={() => {
                handleDisconnect(type);
                setDisconnect(false);
                setType();
              }}
            >
              Remove
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Integrations;
